import { Component, OnInit, inject, ChangeDetectionStrategy } from '@angular/core'
import { EventTypes, LoginResponse, OidcSecurityService, PublicEventsService } from 'angular-auth-oidc-client'

import { MatIconRegistry } from '@angular/material/icon'
import { DomSanitizer } from '@angular/platform-browser'
import { SvgIcon } from './shared/constants/icons.constants'
import { environment } from '../environments/environment'
import { UserService, isRestorablePath } from 'app/core/services/user.service'
import { mountRouter, preventPermissionsGuard, preventRoleGuard } from 'app/core/services/app-loader.service'
import { filter, take } from 'rxjs'
import { Router } from '@angular/router'

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.css'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class AppComponent implements OnInit {
    private oidcSecurityService = inject(OidcSecurityService)
    private matIconRegistry = inject(MatIconRegistry)
    private domSanitizer = inject(DomSanitizer)
    private userService = inject(UserService)
    private eventService = inject(PublicEventsService)
    private router = inject(Router)

    moduleSetup: boolean
    hideLatency: boolean
    preventRouterActivation: boolean = false
    private profileLoaded = false

    get mountRouter() {
        return mountRouter.value
    }

    constructor() {
        this.hideLatency = environment.production
        const value = this.userService.getCookie('fromApp')
        if (value === 'yes' || window.location.hash.includes('id_token')) {
            preventPermissionsGuard.next(true)
            preventRoleGuard.next(true)
            const cookie = this.userService.getCookieData()
            document.cookie = `fromApp=;path=/;domain=${cookie.domain};expires=Thu, 01 Jan 1970 00:00:01 GMT;`
        }
    }

    ngOnInit(): void {
        registerIcons(this.matIconRegistry, this.domSanitizer)

        this.oidcSecurityService.checkAuth().subscribe((loginResponse: LoginResponse) => {
            if (loginResponse.isAuthenticated && loginResponse.userData) {
                this.userService.loadUserInfo(loginResponse).subscribe({
                    next: () => {
                        this.profileLoaded = true
                        // Clear SSO bootstrap latch so Connect/forceRefresh paths can mount.
                        this.preventRouterActivation = false
                        this.userService.startSessionCookieWatch()
                        if (!this.userService.sessionEnded && !mountRouter.value) {
                            this.checkFromUrl()
                        }
                    },
                    error: () => {
                        mountRouter.next(false)
                        this.userService.endSessionAndReauthenticate()
                    }
                })
            } else {
                if (!loginResponse.errorMessage) {
                    this.preventRouterActivation = true
                    this.oidcSecurityService.logoffLocal()
                    if (isRestorablePath(window.location.pathname)) {
                        localStorage.setItem('fromUrl', window.location.pathname)
                    } else {
                        localStorage.removeItem('fromUrl')
                    }
                    this.oidcSecurityService.forceRefreshSession().subscribe((data) => {
                        if (data && data.isAuthenticated) {
                            // Same SPA instance can continue after silent SSO (Connect from public).
                            // Reload remains a fallback when tokens need a clean bootstrap.
                            this.userService.loadUserInfo(data).subscribe({
                                next: () => {
                                    this.profileLoaded = true
                                    this.preventRouterActivation = false
                                    this.userService.startSessionCookieWatch()
                                    if (!this.userService.sessionEnded && !mountRouter.value) {
                                        this.checkFromUrl()
                                    }
                                },
                                error: () => {
                                    setTimeout(() => window.location.reload(), 500)
                                }
                            })
                        } else {
                            this.oidcSecurityService.authorize()
                        }
                    })
                }
            }
        })

        mountRouter.subscribe((data) => {
            if (data) {
                this.moduleSetup = true
            }
        })

        this.eventService
            .registerForEvents()
            .pipe(
                filter((notification) => notification.type === EventTypes.CheckSessionReceived),
                filter((event) => event.value === 'changed'),
                take(1)
            )
            .subscribe(() => {
                mountRouter.next(false)
                this.userService.endSessionAndReauthenticate()
            })

        this.eventService
            .registerForEvents()
            .pipe(
                filter(
                    (notification) =>
                        notification.type === EventTypes.CheckSessionReceived && notification.value === 'unchanged'
                ),
                take(1)
            )
            .subscribe(() => {
                setTimeout(() => {
                    if (
                        this.profileLoaded &&
                        !this.preventRouterActivation &&
                        !mountRouter.value &&
                        !this.userService.sessionEnded
                    ) {
                        this.checkFromUrl()
                    }
                }, 500)
            })
    }

    /**
     * Post-auth bootstrap navigation.
     * - fromUrl: a real internal path (session renew for buyer or MO/SO)
     * - else goingTo: /auctions/… only (Connect / cold buyer entry via AutoLoginGuard)
     * If neither applies, mount current URL; Home remains the thin MO redirect for `/`.
     */
    checkFromUrl(): void {
        if (this.userService.sessionEnded) return

        // Connect / cold open already on /auctions/… — mount in place; drop stale restore keys.
        if (window.location.pathname.startsWith('/auctions/')) {
            localStorage.removeItem('fromUrl')
            localStorage.removeItem('goingTo')
            mountRouter.next(true)
            return
        }

        let fromUrl = localStorage.getItem('fromUrl')
        if (fromUrl && !this.isInternalAppUrl(fromUrl)) {
            localStorage.removeItem('fromUrl')
            fromUrl = null
        }
        const goingTo = localStorage.getItem('goingTo')
        const restoreUrl = this.pickRestoreUrl(fromUrl, goingTo)

        if (!restoreUrl) {
            mountRouter.next(true)
            return
        }

        this.router.navigateByUrl(restoreUrl).then(
            () => {
                this.consumeRestoreUrl(restoreUrl, fromUrl, goingTo)
                if (!this.userService.sessionEnded) mountRouter.next(true)
            },
            () => {
                this.consumeRestoreUrl(restoreUrl, fromUrl, goingTo)
                if (!this.userService.sessionEnded) mountRouter.next(true)
            }
        )
    }

    private pickRestoreUrl(fromUrl: string | null, goingTo: string | null): string | null {
        // Prefer auction fromUrl, then any restorable internal fromUrl (MO/SO), else auction goingTo.
        if (this.isAuctionAppUrl(fromUrl)) return fromUrl
        if (this.isInternalAppUrl(fromUrl)) return fromUrl
        if (this.isAuctionAppUrl(goingTo)) return goingTo
        return null
    }

    private consumeRestoreUrl(restoreUrl: string, fromUrl: string | null, goingTo: string | null): void {
        if (fromUrl && restoreUrl === fromUrl) localStorage.removeItem('fromUrl')
        if (goingTo && restoreUrl === goingTo) localStorage.removeItem('goingTo')
    }

    private isInternalAppUrl(value: string | null): boolean {
        if (!value) return false
        try {
            const url = new URL(value, window.location.origin)
            return url.origin === window.location.origin && url.pathname.startsWith('/') && isRestorablePath(url.pathname)
        } catch {
            return false
        }
    }

    private isAuctionAppUrl(value: string | null): boolean {
        if (!this.isInternalAppUrl(value)) return false
        try {
            return new URL(value!, window.location.origin).pathname.startsWith('/auctions/')
        } catch {
            return false
        }
    }
}

/**
 * Registers all svg icons on first load, so don't have to do it per component.
 * Add icons to the icons.constants.ts file and then to the iconArr array to make them
 * available.
 */
function registerIcons(matIconRegistry: MatIconRegistry, domSanitizer: DomSanitizer) {
    const iconArr: { name: SvgIcon; file: string }[] = [
        { name: 'dollar', file: 'dollar.svg' },
        { name: 'timer', file: 'timer.svg' },
        { name: 'dollar-outlined', file: 'dollar-outlined.svg' },
        { name: 'home', file: 'home.svg' },
        { name: 'cow-goes-moo', file: 'cow-goes-moo.svg' },
        { name: 'going-going-gone', file: 'going-going-gone.svg' },
        { name: 'pick-black', file: 'pick-black.svg' },
        { name: 'pick-white', file: 'pick-white.svg' },
        { name: 'pick-grey', file: 'pick-grey.svg' },
        { name: 'runs-black', file: 'runs-black.svg' },
        { name: 'runs-white', file: 'runs-white.svg' },
        { name: 'runs-grey', file: 'runs-grey.svg' },
        { name: 'highest-bidder', file: 'highest-bidder.svg' },
        { name: 'under-bidder', file: 'under-bidder.svg' },
        { name: 'private-message', file: 'private-message.svg' },
        { name: 'external-link', file: 'external-link.svg' },
        { name: 'notifications-active', file: 'notifications-active.svg' },
        { name: 'notifications-off', file: 'notifications-off.svg' }
    ]

    iconArr.forEach((item) => {
        matIconRegistry.addSvgIcon(
            item.name,
            domSanitizer.bypassSecurityTrustResourceUrl(`app/../assets/icons/${item.file}`)
        )
    })
}
